import { getCentroidCoordinates } from "@phc/common"
import type {
  BiomeshPlatformResponse,
  BiomeshSensorResponse,
} from "@phc/common/gen/threatAnalyticsGen"
import Papa from "papaparse"
import { operationalStatusLabels, sensorTypeLabels } from "../Cop/copUtils"

import { downloadFile } from "../../utils/downloadFile"

interface ExportRow {
  sensor_id: string
  sensor_name: string
  sensor_type: string
  operational_status: string
  sensor_function: string
  sensor_make_model: string
  sensor_notes: string
  last_sample_collected_at: string
  last_sample_result_reported_at: string
  platform_name: string
  platform_latitude: string
  platform_longitude: string
  contacts: string
}

function buildRow(
  sensor: BiomeshSensorResponse,
  platformMap: Map<string, BiomeshPlatformResponse>
): ExportRow {
  const platform = sensor.platform_id
    ? platformMap.get(sensor.platform_id)
    : undefined
  const coords = platform
    ? getCentroidCoordinates(platform.centroid)
    : undefined

  return {
    sensor_id: sensor.sensor_id,
    sensor_name: sensor.sensor_name,
    sensor_type: sensorTypeLabels[sensor.sensor_type],
    operational_status: operationalStatusLabels[sensor.operational_status],
    sensor_function: sensor.sensor_function ?? "",
    sensor_make_model: sensor.sensor_make_model ?? "",
    sensor_notes: sensor.sensor_notes ?? "",
    last_sample_collected_at: sensor.last_sample_collected_at ?? "",
    last_sample_result_reported_at: sensor.last_sample_result_reported_at ?? "",
    platform_name: platform?.name ?? "",
    platform_latitude: coords?.lat.toString() ?? "",
    platform_longitude: coords?.lng.toString() ?? "",
    contacts:
      sensor.contacts
        ?.map(c => `${c.first_name} ${c.last_name}`.trim())
        .filter(name => name.length > 0)
        .join("; ") ?? "",
  }
}

/** Visible for testing — builds the CSV string from sensor + platform data. */
export function buildSensorCSV(
  sensors: BiomeshSensorResponse[],
  platforms: BiomeshPlatformResponse[]
): string {
  const platformMap = new Map(platforms.map(p => [p.platform_id, p]))
  const rows = sensors.map(s => buildRow(s, platformMap))
  return Papa.unparse(rows)
}

export function exportSensorsToCSV(
  sensors: BiomeshSensorResponse[],
  platforms: BiomeshPlatformResponse[],
  filenamePrefix = "biomesh-sensors"
) {
  const csv = buildSensorCSV(sensors, platforms)
  const date = new Date().toISOString().slice(0, 10)
  const filename = `${filenamePrefix}-${date}.csv`
  downloadFile(new Blob([csv], { type: "text/csv;charset=utf-8;" }), filename)
}
